/**
 * Alarm-history card content — the facts one alarm row states on a phone.
 *
 * The history grid's desktop columns are derived, not stored: an alarm row
 * carries its trigger and resolve timestamps and who cleared it, and the rest
 * (label, category, severity, relay loads) resolves from the alarm reference
 * catalog at render time. A phone card has no columns to hang those derived
 * values on, so it restates them as labelled lines — and an alarm that is
 * still active has no resolve time, no duration and no clearer, so it
 * contributes none of those lines rather than three empty ones.
 *
 * Contains:
 * - alarmDurationMs             — how long a resolved alarm stood, in ms
 * - selectAlarmHistoryCardMeta  — row → labelled card entries
 *
 * Returns plain JS — no JSX.
 */

import { selectRowMeta } from '@sophocles/data-grids/components/mobile/cardSections';
import { timestampMs } from '@sophocles/views/sites/components/alarms/alarmLabel';

/**
 * How long an alarm stood between its trigger and its resolution.
 *
 * Null for an alarm that is still active, and for a pair whose timestamps are
 * missing, unparseable, or out of order — a negative duration is a data fault,
 * not a fact worth rendering.
 *
 * @param {object} row - alarm history row.
 * @returns {number|null} Duration in milliseconds, or null.
 */
export function alarmDurationMs(row) {
  if (!row?.resolved_at) return null;
  const start = timestampMs(row.triggered_at);
  const end = timestampMs(row.resolved_at);
  if (!start || !end || end < start) return null;
  return end - start;
}

/**
 * Builds the labelled metadata lines for one alarm history card.
 *
 * Labels reuse the keys the desktop columns carry, so a card and a column read
 * the same alarm the same way.
 *
 * @param {object} row - alarm history row ({ triggered_at, resolved_at, cleared_by }).
 * @param {object} deps
 * @param {Function} deps.t              - i18n translator (key, fallback).
 * @param {Function} deps.formatTimestamp - wire timestamp → display string.
 * @param {Function} deps.formatDuration  - milliseconds → display string ('' when absent).
 * @returns {{key: string, label: string, value: *}[]} Entries for GridMobileCard's `meta`.
 */
export function selectAlarmHistoryCardMeta(row, { t, formatTimestamp, formatDuration }) {
  return selectRowMeta(row, [
    { field: 'triggered_at', label: t('alarms.triggered', 'Triggered'), format: formatTimestamp },
    { field: 'resolved_at', label: t('alarms.resolved', 'Resolved'), format: formatTimestamp },
    {
      key: 'duration',
      field: 'resolved_at',
      label: t('operationsDashboard.duration', 'Duration'),
      format: (_resolvedAt, alarmRow) => formatDuration(alarmDurationMs(alarmRow)),
    },
    { field: 'cleared_by', label: t('alarms.clearedBy', 'Cleared by') },
  ]);
}
